import { apiClient } from "./client";
import { MOCKS_ENABLED } from "./mock/registry";
import { mockDoctors, mockStaff } from "./mock/staff";
import type { PageResponse } from "../types/patient";
import type {
  DoctorLicenseCreateRequest,
  DoctorLicenseResponse,
  DoctorLicenseUpdateRequest,
  DoctorResponse,
  DoctorServiceRef,
  DoctorSummaryResponse,
  DoctorUpdateRequest,
  InviteStatus,
  StaffCreateRequest,
  StaffInviteRequest,
  StaffInviteResponse,
  StaffInviteSummary,
  StaffResponse,
  StaffRole,
  StaffStatus,
  StaffSummaryResponse,
  StaffUpdateRequest,
} from "../types/staff";

export interface StaffListFilter {
  q?: string | null;
  staffRole?: StaffRole | null;
  status?: StaffStatus | null;
  primaryBranchId?: string | null;
}

function buildParams(
  filter: StaffListFilter | undefined,
  pageable: { page?: number; size?: number; sort?: string }
) {
  const p: Record<string, string | number> = {};
  if (filter?.q) p.q = filter.q;
  if (filter?.staffRole) p.staffRole = filter.staffRole;
  if (filter?.status) p.status = filter.status;
  if (filter?.primaryBranchId) p.primaryBranchId = filter.primaryBranchId;
  if (pageable.page != null) p.page = pageable.page;
  if (pageable.size != null) p.size = pageable.size;
  if (pageable.sort) p.sort = pageable.sort;
  return p;
}

const realStaffApi = {
  list(filter: StaffListFilter | undefined, pageable: { page?: number; size?: number; sort?: string }) {
    return apiClient
      .get<PageResponse<StaffSummaryResponse>>("/v1/staff", {
        params: buildParams(filter, pageable),
      })
      .then((r) => r.data);
  },

  get(id: string) {
    return apiClient.get<StaffResponse>(`/v1/staff/${id}`).then((r) => r.data);
  },

  create(req: StaffCreateRequest) {
    return apiClient.post<StaffResponse>("/v1/staff", req).then((r) => r.data);
  },

  update(id: string, req: StaffUpdateRequest) {
    return apiClient.put<StaffResponse>(`/v1/staff/${id}`, req).then((r) => r.data);
  },

  leave(id: string) {
    return apiClient.post<void>(`/v1/staff/${id}/leave`).then(() => undefined);
  },
  returnFromLeave(id: string) {
    return apiClient.post<void>(`/v1/staff/${id}/return`).then(() => undefined);
  },
  terminate(id: string) {
    return apiClient.post<void>(`/v1/staff/${id}/terminate`).then(() => undefined);
  },
  reactivate(id: string) {
    return apiClient.post<void>(`/v1/staff/${id}/reactivate`).then(() => undefined);
  },
  softDelete(id: string) {
    return apiClient.delete<void>(`/v1/staff/${id}`).then(() => undefined);
  },
  /** Hard-delete a staff member. Frozen contract: 204. */
  remove(id: string): Promise<void> {
    return apiClient.delete(`/v1/staff/${id}/permanent`).then(() => undefined);
  },
  restore(id: string) {
    return apiClient.post<void>(`/v1/staff/${id}/restore`).then(() => undefined);
  },

  // Invites
  invite(req: StaffInviteRequest) {
    return apiClient.post<StaffInviteResponse>("/v1/staff/invites", req).then((r) => r.data);
  },
  listInvites(status: InviteStatus = "SENT") {
    return apiClient
      .get<StaffInviteSummary[]>("/v1/staff/invites", { params: { status } })
      .then((r) => r.data);
  },
  resendInvite(id: string) {
    return apiClient
      .post<StaffInviteResponse>(`/v1/staff/invites/${id}/resend`)
      .then((r) => r.data);
  },
  revokeInvite(id: string) {
    return apiClient.post<void>(`/v1/staff/invites/${id}/revoke`).then(() => undefined);
  },
};

// Routes to the mock adapter until the real `/api/v1/staff` is wired
// end-to-end (auth + tenant). Flip via `VITE_USE_MOCKS=false`.
export const staffApi = MOCKS_ENABLED ? mockStaff : realStaffApi;

const realDoctorsApi = {
  list(pageable: { page?: number; size?: number; sort?: string } = {}) {
    return apiClient
      .get<PageResponse<DoctorSummaryResponse>>("/v1/doctors", { params: pageable })
      .then((r) => r.data);
  },
  get(id: string) {
    return apiClient.get<DoctorResponse>(`/v1/doctors/${id}`).then((r) => r.data);
  },
  update(id: string, req: DoctorUpdateRequest) {
    return apiClient
      .put<DoctorResponse>(`/v1/doctors/${id}`, req)
      .then((r) => r.data);
  },
  /** Toggle prescriber status (Được kê đơn thuốc). */
  setPrescriber(id: string, on: boolean): Promise<void> {
    return on
      ? apiClient.post<void>(`/v1/doctors/${id}/activate-prescribing`).then(() => undefined)
      : apiClient.delete<void>(`/v1/doctors/${id}/prescribing`).then(() => undefined);
  },

  /** Short-lived URL of the doctor's uploaded signature image (printed on Rx). */
  signatureUrl(id: string): Promise<string | null> {
    return apiClient
      .get<{ url: string | null }>(`/v1/doctors/${id}/signature-url`)
      .then((r) => r.data.url);
  },
  uploadSignature(id: string, file: File): Promise<string> {
    const fd = new FormData();
    fd.append("file", file);
    return apiClient
      .post<{ url: string }>(`/v1/doctors/${id}/signature`, fd)
      .then((r) => r.data.url);
  },

  /** Services this doctor provides (assignment set). */
  listServices(doctorId: string) {
    return apiClient
      .get<DoctorServiceRef[]>(`/v1/doctors/${doctorId}/services`)
      .then((r) => r.data);
  },
  /** Replace the whole set of services this doctor provides. */
  setServices(doctorId: string, serviceIds: string[]) {
    return apiClient
      .put<void>(`/v1/doctors/${doctorId}/services`, { serviceIds })
      .then(() => undefined);
  },

  listLicenses(doctorId: string) {
    return apiClient
      .get<DoctorLicenseResponse[]>(`/v1/doctors/${doctorId}/licenses`)
      .then((r) => r.data);
  },
  createLicense(doctorId: string, req: DoctorLicenseCreateRequest) {
    return apiClient
      .post<void>(`/v1/doctors/${doctorId}/licenses`, req)
      .then(() => undefined);
  },
  updateLicense(doctorId: string, licenseId: string, req: DoctorLicenseUpdateRequest) {
    return apiClient
      .put<void>(`/v1/doctors/${doctorId}/licenses/${licenseId}`, req)
      .then(() => undefined);
  },
  verifyLicense(doctorId: string, licenseId: string, via: string) {
    return apiClient
      .post<void>(`/v1/doctors/${doctorId}/licenses/${licenseId}/verify`, { via })
      .then(() => undefined);
  },
  deactivateLicense(doctorId: string, licenseId: string) {
    return apiClient
      .delete<void>(`/v1/doctors/${doctorId}/licenses/${licenseId}`)
      .then(() => undefined);
  },
  expiringLicenses(days = 60) {
    return apiClient
      .get<DoctorLicenseResponse[]>("/v1/doctors/expiring-licenses", { params: { days } })
      .then((r) => r.data);
  },
};

export const doctorsApi = MOCKS_ENABLED ? mockDoctors : realDoctorsApi;

export const staffQueryKeys = {
  all: ["staff"] as const,
  list: (filter: StaffListFilter | undefined, page: number) =>
    ["staff", "list", filter ?? {}, page] as const,
  detail: (id: string) => ["staff", "detail", id] as const,
  invites: (status: InviteStatus) => ["staff", "invites", status] as const,
  doctors: () => ["doctors"] as const,
  doctorLicenses: (doctorId: string) => ["doctors", doctorId, "licenses"] as const,
  doctorServices: (doctorId: string) => ["doctors", doctorId, "services"] as const,
  expiringLicenses: (days: number) => ["doctors", "expiring-licenses", days] as const,
};
